/**
 * Resolving a holding's name.
 *
 * A name was stored on every holding row, which meant 1,379 strings for 629
 * distinct companies — Royal Bank's name written out once for each of the
 * fifteen funds that hold it. Names now live in one table for the dataset and a
 * holding carries only its ticker.
 *
 * Rows with no ticker keep an index into a second, small table. Those are the
 * cash and written-option lines — "Cash and other assets and liabilities" is 83
 * of them — plus a handful the parser could not match a ticker to.
 *
 * Resolution is per row, on demand. A page showing ten holdings looks up ten
 * names; nothing walks the dataset turning codes back into strings, because
 * almost no row is ever asked.
 */

export interface NameTables {
    /** Ticker to the name we publish for it. */
    bySymbol: Record<string, string>;
    /** Names for rows that have no ticker, addressed by index. */
    unlisted: string[];
}

/**
 * The name for one holding.
 *
 * `symbol` wins when present. `ref` addresses the unlisted table and is only
 * meaningful when there is no symbol. Returns null rather than a placeholder
 * when neither resolves — a caller showing "—" is better than one showing a
 * ticker where a company name belongs.
 */
export const nameOf = (tables: NameTables, symbol: string | null, ref: number | null): string | null => {
    // A ticker shared by two companies is deliberately absent from the table —
    // EDV is both a gold miner and a Vanguard bond fund — so those rows carry
    // their own name and fall through to the reference like the cash lines do.
    if (symbol && symbol in tables.bySymbol) return tables.bySymbol[symbol];
    if (ref != null) return tables.unlisted[ref] ?? null;
    return symbol;
};

/** Does this holding need a name of its own, or can the table answer? */
export const needsRef = (tables: NameTables, symbol: string | null): boolean => !symbol || !(symbol in tables.bySymbol);

/**
 * Names differ between issuers for the same company — "Agnico Eagle Mines Ltd"
 * against "Agnico Eagle Mines Limited", "Apple Inc" against "Apple Inc." — so
 * one has to be chosen for the table.
 *
 * Longest wins. That is not a style preference: six names arrive truncated at
 * exactly thirty characters ("Canadian Pacific Kansas City L"), and preferring
 * the shorter would enshrine the truncation. Where lengths tie, the Bloomberg
 * style that moves the article to the end — "Bank of Nova Scotia/The" — loses to
 * the one that reads as a name.
 */
/**
 * Do two published names describe the same company?
 *
 * A ticker is not a unique key. EDV is Endeavour Mining on one exchange and the
 * Vanguard Extended Duration Treasury ETF on another, and both are held — so a
 * name table keyed on ticker alone would file a gold miner under a bond fund.
 *
 * Comparison happens after normalising the three ways issuers differ: the
 * Bloomberg habit of moving the article to the end ("Coca-Cola Co/The"),
 * abbreviation ("Cos Ltd" against "Companies Limited"), and truncation at
 * thirty characters. Once those are undone, one name being a prefix of the
 * other means one company; anything else means two.
 */
const EXPANSIONS: [RegExp, string][] = [
    [/\bcos\b/g, 'companies'],
    [/\bco\b/g, 'company'],
    [/\bcorp\b/g, 'corporation'],
    [/\bltd\b/g, 'limited'],
    [/\binc\b/g, 'incorporated'],
    [/\bintl\b/g, 'international'],
    [/&/g, 'and'],
];

const normalizeName = (raw: string): string => {
    let s = raw.toLowerCase().trim();
    // "coca-cola co/the" -> "the coca-cola co"
    s = s.replace(/^(.*?)[,\s]*\/\s*the$/, 'the $1');
    for (const [pattern, full] of EXPANSIONS) s = s.replace(pattern, full);
    s = s.replace(/^the\s+/, '');
    return s.replace(/[^a-z0-9]/g, '');
};

export const sameCompany = (a: string, b: string): boolean => {
    const [x, y] = [normalizeName(a), normalizeName(b)];
    return x.startsWith(y) || y.startsWith(x);
};

/**
 * Group a ticker's published names into companies. More than one group means
 * the ticker is shared and cannot be looked up by symbol alone.
 */
export const clusterNames = (names: string[]): string[][] => {
    const clusters: string[][] = [];
    for (const name of names) {
        const hit = clusters.find((c) => c.some((other) => sameCompany(other, name)));
        if (hit) hit.push(name);
        else clusters.push([name]);
    }
    return clusters;
};

export const canonicalName = (variants: string[]): string =>
    [...variants].sort((a, b) => b.length - a.length || Number(/\/The$/.test(a)) - Number(/\/The$/.test(b)) || a.localeCompare(b))[0];
